/**
 * Template loading and hydration utilities
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { loadFromIndexedDB } from "../../../util/indexedDBUtils";

/**
 * Ensure the canvas elements store exists in IndexedDB
 * @returns {Promise<void>}
 */
export const ensureCanvasElementsStore = async () => {
  try {
    // First open to read current version and stores
    const db = await new Promise((resolve, reject) => {
      const req = window.indexedDB.open("canvasElementsDB");
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror = (e) => reject(e.target.error);
    });
    if (db.objectStoreNames.contains("canvasElements")) {
      db.close();
      return; // Store already exists
    }
    const currentVersion = db.version || 1;
    db.close();
    // Re-open with a higher version to trigger onupgradeneeded and create the store
    await new Promise((resolve, reject) => {
      const upgradeReq = window.indexedDB.open(
        "canvasElementsDB",
        currentVersion + 1
      );
      upgradeReq.onupgradeneeded = (event) => {
        const udb = event.target.result;
        if (!udb.objectStoreNames.contains("canvasElements")) {
          udb.createObjectStore("canvasElements", { keyPath: "id" });
        }
      };
      upgradeReq.onsuccess = (e) => {
        e.target.result.close();
        resolve();
      };
      upgradeReq.onerror = (e) => reject(e.target.error);
      upgradeReq.onblocked = () =>
        reject(new Error("Database upgrade blocked"));
    });
  } catch (e) {
    // Silently fail
  }
};

/**
 * Hydrate canvas pages with elements from IndexedDB
 * @param {Array} canvasPages - Array of canvas pages
 * @param {Function} setCanvasPages - Function to update canvas pages
 * @returns {Promise<void>}
 */
export const hydrateElements = async (canvasPages, setCanvasPages) => {
  await ensureCanvasElementsStore();
  const hydratedPages = await Promise.all(
    canvasPages.map(async (page) => {
      try {
        const result = await loadFromIndexedDB("canvasElements", page.id);
        const elements = result?.data || result || {};

        return {
          ...page,
          zoomLevel: page.zoomLevel || 100, // Ensure zoomLevel is always set
          snapshot: elements.snapshot,
          elements: {
            texts: elements.texts || [],
            rectangles: elements.rectangles || [],
            circles: elements.circles || [],
            arrows: elements.arrows || [],
            scribbles: elements.scribbles || [],
            lines: elements.lines || [],
            doubleSidedArrows: elements.doubleSidedArrows || [],
            images: elements.images || [],
            canvasHeight: elements.canvasHeight, // Preserve canvasHeight from IndexedDB
          },
        };
      } catch (error) {
        return {
          ...page,
          zoomLevel: page.zoomLevel || 100, // Ensure zoomLevel is always set
          elements: {
            texts: [],
            rectangles: [],
            circles: [],
            arrows: [],
            scribbles: [],
            lines: [],
            doubleSidedArrows: [],
            images: [],
          },
        };
      }
    })
  );

  setCanvasPages(hydratedPages);
};

/**
 * Get valid value from localStorage (filters out "undefined" string)
 * @param {string} key - localStorage key
 * @returns {string|null} Valid value or null
 */
export const getValidLocalStorageValue = (key) => {
  const value = localStorage.getItem(key);
  return value && value !== "undefined" ? value : null;
};

/**
 * Deduplicate elements by id or shape_id (lighter version)
 * @param {Array} arr - Array of elements
 * @returns {Array} Deduplicated array
 */
export const uniqueById = (arr = []) => {
  const map = new Map();
  (arr || []).forEach((el) => {
    const key = el?.id || el?.shape_id;
    if (!key) return;
    if (!map.has(key)) map.set(key, el);
  });
  return Array.from(map.values());
};
